import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "@/auth/SessionProvider";
import {
  ensureOneOrgContext,
  peekOneOrgContext,
  resetOneOrgContextCache,
} from "@/lib/ensureOneOrgContext";

export type OneOrgContextStatus = "loading" | "ready" | "missing";

type OneOrgContextValue = {
  orgContextStatus: OneOrgContextStatus;
  orgContextError: string | null;
  orgId: string | null;
};

const OneOrgContext = createContext<OneOrgContextValue>({
  orgContextStatus: "loading",
  orgContextError: null,
  orgId: null,
});

export function OneOrgContextProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [orgContextStatus, setOrgContextStatus] = useState<OneOrgContextStatus>("loading");
  const [orgContextError, setOrgContextError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (session.status !== "authenticated") {
      resetOneOrgContextCache();
      setOrgContextStatus(session.status === "loading" ? "loading" : "missing");
      setOrgContextError(null);
      setOrgId(null);
      return;
    }

    let cancelled = false;
    if (!peekOneOrgContext()?.orgId) {
      setOrgContextStatus("loading");
      setOrgContextError(null);
    }

    void (async () => {
      try {
        const result = await ensureOneOrgContext(session.data?.user?.orgId);
        if (cancelled) return;
        if (result.orgId) {
          setOrgId(result.orgId);
          setOrgContextStatus("ready");
          setOrgContextError(null);
        } else {
          setOrgId(null);
          setOrgContextStatus("missing");
          setOrgContextError(result.error || "Organization context required.");
        }
      } catch (e) {
        if (cancelled) return;
        setOrgId(null);
        setOrgContextStatus("missing");
        setOrgContextError(e instanceof Error ? e.message : "Could not resolve organization.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session.status, session.data?.user?.orgId]);

  const value = useMemo(
    () => ({ orgContextStatus, orgContextError, orgId }),
    [orgContextStatus, orgContextError, orgId],
  );

  return <OneOrgContext.Provider value={value}>{children}</OneOrgContext.Provider>;
}

export function useOneOrgContext(): OneOrgContextValue {
  return useContext(OneOrgContext);
}
